import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useInvoiceDetails } from 'entities/invoice/invoice.hook';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { useTitleHook } from 'hooks/page-title.hook';
import { RelatedPayorders } from 'entities/invoice-report/related-payorders';
import { ProfitCard } from 'entities/invoice-report/profit-card';
import { RelatedInvoices } from 'entities/invoice-report/related-invoices';
import { PageHeader } from 'shared/ui/details-page-header';
import { useIdParam } from 'utils/url-paramters.hook';
import { useInvoiceTotals } from 'entities/invoice/totals';
import { InvoiceReportRows } from 'entities/invoice-report/invoice-report-rows';
import { useInvoiceReportRows } from 'entities/invoice-report/invoice-report.hook';
import { AVAILABLE_FOR_CHANGE_CANCEL_STATUSES, INVOICE_TYPE_CLIENT, PayOrderDto } from '@sr/dto';
import { calculateInvoiceReportTotals } from 'entities/invoice-report/lib';
import { InvoiceCloseForm } from '../../features/invoice/invoice-close/invoice-close.form';
import { ErrorBanner } from 'shared/ui/error-banner';
import { useInvoiceBasics } from 'entities/invoice/mutable-data/lib';
import { InvoiceTitle } from 'features/invoice/title/title';
import { InvoiceModeActions } from 'widgets/invoice/mode-actions/invoice-mode-actions';
import { InvoiceStatusSpan } from 'entities/invoice/status-span/status-span';
import { InvoiceCancelStatus } from 'features/invoice/invoice-cancel-status';
import { AgentCard } from 'entities/agent-card/agent-card';

const InvoiceReportPage = () => {
  const invoiceId = useIdParam('invoiceId');
  const { data: invoiceBasics } = useInvoiceBasics(invoiceId);
  const { invoice } = useInvoiceDetails(invoiceId);
  const { totals: invoiceTotals } = useInvoiceTotals(invoiceId);
  const { report, error, isLoading: supplierPaymerntLoading, isFetching: reportFetching } = useInvoiceReportRows(invoiceId);

  useTitleHook(invoice && `Отчет по счету ${invoice.invoiceNumber} для ${invoice.buyerCompany.name}`);

  const rows = report?.rows || [];
  const supplierPayments = report?.rows.reduce<PayOrderDto[]>((acc, row) => {
    if (row.sellerPayments) return acc.concat(...row.sellerPayments);
    return acc;
  }, []);

  if (!invoice) return <LoadingBanner />;
  if (error) return <ErrorBanner errorMessage={error} />;

  const totals = calculateInvoiceReportTotals(rows);

  return (
    <>
      <PageHeader
        title={<InvoiceTitle invoice={invoice} basics={invoiceBasics} />}
        actions={<InvoiceModeActions invoice={invoice} mode="report" />}
        subHeader={
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="body1" component="span">Статус: </Typography>
              <InvoiceStatusSpan status={invoice.status} size="medium" />
            </Box>
            {AVAILABLE_FOR_CHANGE_CANCEL_STATUSES.includes(invoice.status) &&
              <InvoiceCancelStatus invoiceId={invoice.id} />}
            {invoice.isInternetAdvertising &&
              <Chip label="Реклама в интернете" size="small" color="info" variant="outlined" />}
          </Stack>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: invoiceTotals ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'stretch'
        }}
      >
        {invoiceTotals && (
          <Box sx={{ minWidth: 0, display: 'flex', '& > .MuiPaper-root': { flex: 1, minWidth: 0 } }}>
            <ProfitCard invoice={invoice} profits={totals} showProfits={invoice.type === INVOICE_TYPE_CLIENT} />
          </Box>
        )}
        <Stack spacing={2} sx={{ minWidth: 0, '& > .MuiPaper-root:last-child': { flex: 1 } }}>
          <InvoiceCloseForm totals={totals} rows={rows} invoiceId={invoiceId} invoiceType={invoice.type} />
          <AgentCard title="Покупатель" company={invoice.buyerCompany} legalEntity={invoice.buyer} />
          <AgentCard title="Продавец" company={invoice.sellerCompany} legalEntity={invoice.seller} />
        </Stack>
      </Box>
      <Box marginTop={2}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="invoice-services-content"
            id="invoice-services-header"
          >
            <Typography variant="h6">Услуги</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }} aria-busy={reportFetching}>
            {reportFetching
              ? <LoadingBanner />
              : <InvoiceReportRows invoice={invoice} rows={rows} totals={totals} />}
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box marginTop={2}>
        <Typography variant="h6" gutterBottom>
          Платежи по счету
        </Typography>
        <RelatedPayorders
          payOrders={{
            error: error,
            isLoading: false,
            items: report?.clientPayments.flatMap((p) => p) ?? [],
            reload: () => {}
          }}
          referenceInvoiceId={invoiceId}
        />
      </Box>
      <Box marginTop={2}>
        <Typography variant="h6" gutterBottom>
          Платежи по связным счетам
        </Typography>
        {supplierPayments && (
          <RelatedPayorders
            payOrders={{
              error: error,
              isLoading: supplierPaymerntLoading,
              items: supplierPayments,
              reload: () => {}
            }}
            referenceInvoiceId={invoiceId}
          />
        )}
      </Box>
      <Box marginTop={2}>
        <Typography variant="h6" gutterBottom>
          Связанные счета
        </Typography>
        <RelatedInvoices invoiceId={invoiceId} />
      </Box>
    </>
  );
};

export default InvoiceReportPage;
